import axios, { AxiosResponse } from 'axios';
import createAuthRefreshInterceptor, {
  AxiosAuthRefreshRequestConfig,
} from 'axios-auth-refresh';
import Qs from 'qs';
import { getCookie } from 'utils/functions/cookies';
import { resetSession } from 'utils/functions/resetSession';

// declares the axios instance to be used for letrusAPI communication
const letrusAPIClient = axios.create({
  timeout: 360 * 1000,
  baseURL:
    import.meta.env.REACT_APP_API_URL || 'https://api-prod.letrus.com.br',
  paramsSerializer: queryParamsSerializer,
});

// filters null and repeated values from the query params and keep them on the right format
function queryParamsSerializer(params: Record<string, unknown>) {
  return Qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true });
}

// configures the interceptor to refresh the token
createAuthRefreshInterceptor(letrusAPIClient, async (failedRequest) => {
  try {
    const { data: newToken } = await letrusAPI('/api/v1/token/refresh/', {
      method: 'post',
      skipAuthRefresh: true,
    });

    failedRequest.response.config.headers['x-csrftoken'] = newToken;

    return Promise.resolve();
  } catch (_) {
    resetSession();
    window.dispatchEvent(new CustomEvent('invalidAuth', { detail: 'invalid' }));

    return Promise.reject(new Error('Token refresh failed'));
  }
});

function isHTMLResponse(headers?: AxiosResponse<unknown>['headers']) {
  const contentType = headers?.['content-type'] || '';

  return typeof contentType === 'string' && contentType.includes('text/html');
}

// response interceptor: ignore text/html responses and handle network status
letrusAPIClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const response = error?.response;

    // backend Django is configured to return html on some errors,
    // and it is breaking the frontend so we have to ignore those responses.
    // The ideal solution is for the API to return only jsons, but that is not going to happen...
    if (response && isHTMLResponse(response?.headers)) {
      error.response.data = { detail: 'Resposta não encontrada.(html)' };
    }

    if (error?.message === 'Network Error') {
      window.dispatchEvent(
        new CustomEvent('networkStatus', { detail: 'offline' })
      );
    }

    return Promise.reject(error);
  }
);

// returns a configured axios instance,  bearing the correct auth token
function letrusAPI(url: string, axiosParams?: AxiosAuthRefreshRequestConfig) {
  const customHeaders = {
    ...axiosParams?.headers,
    'x-csrftoken': getCookie('csrftoken'),
    'Content-Type': 'application/json',
  };

  return letrusAPIClient({
    url,
    headers: customHeaders,
    withCredentials: true,
    ...axiosParams,
  });
}

export default letrusAPI;
